import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '@features/auth/hooks/useAdminAuth';
import { useUI } from '@shared/hooks/useUI';
import { getImageUrl } from '@shared/utils/helpers';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from '@shared/components/ui/Modal';
import { Button } from '@shared/components/ui/Button';

/**
 * Admin Header Component
 * Pixel-perfect recreation of Angular header.component.html
 * Source: /FrontEnd/src/app/common/header/header.component.html
 */
export const AdminHeader = () => {
    const { currentAdmin, logout } = useAdminAuth();
    const { sidebarOpen, toggleSidebar, isProfileOpen, toggleProfile } = useUI();
    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

    const getProfileImageUrl = (url?: string) => {
        return getImageUrl(url);
    };

    return (
        <div
            className={`header-area bg-white dark:bg-[#0c1427] py-[13px] px-[20px] md:px-[25px] fixed top-0 z-[50] rounded-b-md transition-all border-b border-gray-100 dark:border-[#172036] flex items-center justify-between ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}
            id="header-area"
        >
            <div className="flex items-center">
                <button
                    type="button"
                    className="hide-sidebar-toggle transition-all inline-flex items-center justify-center hover:text-primary-500 mr-[10px] md:mr-[20px] w-10 h-10 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={toggleSidebar}
                >
                    <i className="ri-menu-line text-[22px]"></i>
                </button>
                <span className="font-semibold text-black dark:text-white text-lg hidden sm:block">
                    Dashboard
                </span>
            </div>

            <ul className="flex items-center">
                {/* User Profile */}
                <li className="relative profile-menu">
                    <button
                        type="button"
                        className={`flex items-center relative pr-[22px] text-black dark:text-white ${isProfileOpen ? 'active' : ''}`}
                        id="profileMenuBtn"
                        onClick={toggleProfile}
                    >
                        <div className="relative w-[38px] h-[38px] md:w-[42px] md:h-[42px] rounded-full border-[2px] border-primary-100 p-[2px] mr-[10px]">
                            <img
                                src={getProfileImageUrl(currentAdmin?.profileImageUrl)}
                                className="w-full h-full rounded-full object-cover bg-gray-100"
                                alt="Admin"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=' + (currentAdmin?.firstName || 'A') + '&background=605DFF&color=fff';
                                }}
                            />
                        </div>
                        <span className="font-bold text-sm md:text-base mr-[5px] hidden md:block">
                            {currentAdmin?.firstName || 'User'}
                        </span>
                        <i className="ri-arrow-down-s-line text-[18px] absolute right-0 top-1/2 -translate-y-1/2 mt-px"></i>
                    </button>

                    {/* Profile Menu Dropdown */}
                    {isProfileOpen && (
                        <div className="profile-menu-dropdown bg-white dark:bg-[#0c1427] transition-all py-[20px] absolute mt-[15px] w-[250px] z-[1] top-full right-0 rounded-md border border-gray-100 dark:border-[#172036] shadow-3xl">
                            <div className="flex items-center border-b border-gray-100 dark:border-[#172036] pb-[15px] mx-[20px] mb-[10px]">
                                <div className="w-[48px] h-[48px] rounded-full border-2 border-primary-100 p-[2px] mr-[12px]">
                                    <img
                                        src={getProfileImageUrl(currentAdmin?.profileImageUrl)}
                                        className="rounded-full w-full h-full object-cover bg-gray-100"
                                        alt="Admin Avatar"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=' + (currentAdmin?.firstName || 'A') + '&background=605DFF&color=fff';
                                        }}
                                    />
                                </div>
                                <div className="overflow-hidden">
                                    <span className="block text-black dark:text-white font-bold text-[15px] truncate">
                                        {currentAdmin?.firstName} {currentAdmin?.lastName}
                                    </span>
                                    <span className={`block text-[11px] px-2 py-0.5 rounded-full mt-1 font-semibold inline-block ${currentAdmin?.role === 'SuperAdmin' ? 'bg-purple-50 text-purple-600' :
                                        currentAdmin?.role === 'Admin' ? 'bg-blue-50 text-blue-600' :
                                            'bg-green-50 text-green-600'
                                        }`}>
                                        {currentAdmin?.role || 'Admin'}
                                    </span>
                                </div>
                            </div>
                            <ul className="pt-[5px]">
                                <li>
                                    <Link
                                        to="/dashboard/profile"
                                        className="flex items-center relative py-[12px] px-[20px] text-gray-700 dark:text-gray-300 transition-all hover:text-primary-500 hover:bg-gray-50 dark:hover:bg-[#172036] font-semibold"
                                        onClick={toggleProfile}
                                    >
                                        <i className="ri-user-line mr-[12px] text-[19px] text-gray-400"></i>
                                        My Profile
                                    </Link>
                                </li>
                                <li>
                                    <button
                                        type="button"
                                        onClick={() => setIsLogoutDialogOpen(true)}
                                        className="w-full flex items-center relative py-[12px] px-[20px] text-red-500 transition-all hover:text-red-600 hover:bg-red-50 dark:hover:bg-[#172036] cursor-pointer font-semibold"
                                    >
                                        <i className="ri-logout-box-r-line mr-[12px] text-[19px]"></i>
                                        Logout
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                </li>
            </ul>

            {/* Logout Confirmation Dialog */}
            <Dialog open={isLogoutDialogOpen} onOpenChange={setIsLogoutDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Sign Out</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to log out of the admin panel? You will need to sign in again to access the dashboard.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setIsLogoutDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => {
                                setIsLogoutDialogOpen(false);
                                logout();
                                toggleProfile();
                            }}
                        >
                            Log Out
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
